import { focusManager, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AuthUser } from "aws-amplify/auth";
import * as React from "react";

import {
	type CurrentPartner,
	getCurrentPartnerQueryFn,
	getCurrentPartnerQueryKey,
} from "../queries/use-get-current-partner";
import { getCurrentUser, signOut as amplifySignOut } from "./session";

interface PartnerAuthContextValue {
	isLoading: boolean;
	authCognitoUser: AuthUser | undefined;
	partner: CurrentPartner | undefined;
	isAuthenticated: boolean;
	refreshSession: () => Promise<{
		authCognitoUser: AuthUser | undefined;
		partner: CurrentPartner | undefined;
	}>;
	signOut: () => Promise<void>;
}

const PartnerAuthContext = React.createContext<PartnerAuthContextValue>({
	isLoading: true,
	authCognitoUser: undefined,
	partner: undefined,
	isAuthenticated: false,
	refreshSession: () =>
		Promise.resolve({ authCognitoUser: undefined, partner: undefined }),
	signOut: () => Promise.resolve(),
});

interface PartnerAuthProviderProps {
	children: React.ReactNode;
}

export const PartnerAuthProvider: React.FC<PartnerAuthProviderProps> = ({
	children,
}) => {
	const queryClient = useQueryClient();
	const { refetch: refetchCurrentPartner } = useQuery({
		queryKey: getCurrentPartnerQueryKey(),
		queryFn: ({ signal }) => getCurrentPartnerQueryFn(signal),
		enabled: false,
	});
	const [isLoading, setIsLoading] = React.useState(true);
	const [authCognitoUser, setAuthCognitoUser] = React.useState<
		AuthUser | undefined
	>(undefined);
	const [partner, setPartner] = React.useState<CurrentPartner | undefined>(
		undefined,
	);

	const refreshSession = React.useCallback(async () => {
		const currentUser = await getCurrentUser().catch(() => undefined);

		if (!currentUser) {
			setAuthCognitoUser(undefined);
			setPartner(undefined);
			setIsLoading(false);
			return { authCognitoUser: undefined, partner: undefined };
		}

		const currentPartner = await refetchCurrentPartner()
			.then((result) => result.data)
			.catch(() => undefined);

		if (!currentPartner) {
			try {
				await amplifySignOut();
			} catch (error) {
				console.error("Partner fail-closed sign-out failed", error);
			}
			setAuthCognitoUser(undefined);
			setPartner(undefined);
			setIsLoading(false);
			return { authCognitoUser: undefined, partner: undefined };
		}

		setAuthCognitoUser(currentUser);
		setPartner(currentPartner);
		setIsLoading(false);
		return { authCognitoUser: currentUser, partner: currentPartner };
	}, [refetchCurrentPartner]);

	React.useEffect(() => {
		void refreshSession();

		const unsubscribe = focusManager.subscribe((isVisible) => {
			if (isVisible) void refreshSession();
		});

		return unsubscribe;
	}, [refreshSession]);

	const signOut = React.useCallback(async () => {
		await amplifySignOut();
		queryClient.clear();
		setAuthCognitoUser(undefined);
		setPartner(undefined);
		setIsLoading(false);
	}, [queryClient]);

	return (
		<PartnerAuthContext.Provider
			value={{
				isLoading,
				authCognitoUser,
				partner,
				isAuthenticated: !!authCognitoUser && !!partner,
				refreshSession,
				signOut,
			}}>
			{children}
		</PartnerAuthContext.Provider>
	);
};

export const usePartnerAuth = () => React.useContext(PartnerAuthContext);
